import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import FieldSet from 'flarum/common/components/FieldSet';
import Alert from 'flarum/common/components/Alert';

/**
 * Club fields on the settings page: Discord link status, RetroAchievements
 * handle, and club standing. All read-only.
 *
 * Nothing here collects a Discord identity any more. Linking is FoF OAuth's
 * job, from the security page, because only Discord can confirm who somebody
 * is - a handle typed into a box could be anyone's.
 *
 * NOTE on the extend() calls below: Flarum 2.0 lazy-loads pages and modals
 * as separate chunks, so importing SettingsPage directly gives `undefined` at
 * init time and `extend(Component.prototype, ...)` throws ("can't access
 * property prototype"), which aborts the whole initializer. 2.0's extend()
 * therefore accepts the MODULE PATH as a string and applies the extension
 * when that chunk actually loads.
 */

/**
 * Flarum group ids in Discord's own role order, highest first.
 *
 * Taken from the guild's actual role positions rather than assumed: the order
 * is not the one intuition suggests. Grand Slam sits BELOW Triple Crown, and
 * First Clear sits ABOVE Beaten (Hardcore). Guessing produced a different
 * order in two places.
 *
 * Discord colours a name by the member's highest role that has a colour, and
 * this list is what reproduces that. Positions on Discord at the time of
 * writing are in the comments, for anyone reconciling the two later.
 */
const GROUP_PRIORITY = [1,
// Master of the Joeyverse / Admin  (35)
4,
// Mods                             (34)
16,
// Perfect Season - Hardcore       (33)
15,
// Perfect Season - Softcore       (32)
14,
// Perfect Season                  (31)
13,
// Triple Crown - Hardcore         (30)
12,
// Triple Crown - Softcore         (29)
11,
// Triple Crown                    (28)
17,
// Grand Slam                      (27)
10,
// First Master                    (26)
9,
// Mastery (Hardcore)               (25)
8,
// Mastery                          (24)
5,
// First Clear                      (23)
7,
// Beaten (Hardcore)                (22)
6 // Beaten                           (17)
];

/**
 * The id of the highest-ranked coloured group this member holds, or null.
 *
 * Returns an id rather than a colour so the stylesheet can own the actual
 * value: Discord's palette is built for a dark client, and four of these
 * colours fall below 3:1 against white - #FFD700 manages 1.40:1, which is
 * not readable text. The LESS carries the exact Discord colour for dark mode
 * and a darkened-but-same-hue variant for light. An inline colour here would
 * make that impossible.
 */
function rankedGroupId(user) {
  var _GROUP_PRIORITY$find;
  if (!user || typeof user.groups !== 'function') return null;
  const groups = user.groups() || [];
  const held = new Set();
  groups.forEach(g => {
    // A group with no colour is skipped rather than counted, exactly as
    // Discord skips uncoloured roles when picking a name colour.
    if (g && typeof g.id === 'function' && g.color && g.color()) {
      held.add(Number(g.id()));
    }
  });
  return (_GROUP_PRIORITY$find = GROUP_PRIORITY.find(id => held.has(id))) != null ? _GROUP_PRIORITY$find : null;
}

/**
 * Flarum's own Alert, with dismissal that persists.
 *
 * AlertManager renders every alert as
 *   <componentClass {...attrs} ondismiss={dismiss this key} />
 * so an ondismiss passed in through attrs is always overwritten - the X
 * closed the notice and saved nothing, and it returned on the next page load.
 *
 * Re-wrapped per render rather than once in oninit, because Mithril rebuilds
 * attrs on every redraw and a one-time wrap would be thrown away with them.
 */
class ConnectPromptAlert extends Alert {
  view(vnode) {
    const closeAlert = this.attrs.ondismiss;
    this.attrs.ondismiss = () => {
      if (app.session.user) {
        app.session.user.savePreferences({
          jrhDiscordPromptDismissed: true
        });
      }
      if (closeAlert) closeAlert();
    };
    return super.view(vnode);
  }
}

/**
 * One-time notice asking an unlinked member to connect Discord.
 *
 * Shown once, dismissed forever. The dismissal is a user PREFERENCE, saved
 * server-side, so it holds on every device and survives a cleared browser -
 * a member with no Discord account gets asked exactly once in their life.
 *
 * Deliberately not a modal on signup: Flarum's registration has several exit
 * paths (email confirmation chief among them), so a member can easily never
 * see that moment. A notice keyed on "not linked" catches them whenever they
 * do arrive.
 */
function maybeShowConnectPrompt() {
  const user = app.session.user;
  if (!user) return;
  if (user.attribute('discordVerified')) return;
  if (user.preferences() && user.preferences().jrhDiscordPromptDismissed) return;

  // No control buttons: the action is a plain link inside the sentence, and
  // dismissal is Flarum's own X. Two buttons in an Alert never laid out well
  // at any width - they took a row of their own, truncated their labels, or
  // collided with the X.
  app.alerts.show(ConnectPromptAlert, {
    type: 'warning',
    className: 'jrh-discordPrompt',
    dismissible: true
  }, [app.translator.trans('jrh-discord-link.forum.prompt.body'), ' ', m("a", {
    href: app.route('user.security', {
      username: user.slug()
    })
  }, app.translator.trans('jrh-discord-link.forum.prompt.connect'))]);
}
app.initializers.add('jrh-discord-link', () => {
  // Colour usernames by rank, the way Discord does. Badges are left alone -
  // Flarum shows all of them and that stays, so the forum carries strictly
  // more information than Discord rather than less.
  // Hooked on the components that RENDER a username, not on a Username
  // component - there isn't one. Flarum's username is a helper function
  // returning <span class="username">, with nothing in the markup saying whose
  // it is, so neither a component extension nor CSS alone can reach it. These
  // put the rank on an ancestor and let the stylesheet target .username inside.
  const rankOnRoot = getUser => function (vdom) {
    const id = rankedGroupId(getUser.call(this));
    if (id === null || !vdom || !vdom.attrs) return;
    vdom.attrs.className = "".concat(vdom.attrs.className || '', " jrh-rank jrh-rank--g").concat(id);
  };
  extend('flarum/forum/components/PostUser', 'view', rankOnRoot(function () {
    return this.attrs.post && this.attrs.post.user && this.attrs.post.user();
  }));
  extend('flarum/forum/components/UserCard', 'view', rankOnRoot(function () {
    return this.attrs.user;
  }));
  extend('flarum/forum/components/SettingsPage', 'settingsItems', function (items) {
    const user = app.session.user;
    if (!user) return;
    const verified = user.attribute('discordVerified');

    // Linked or not - there is no third state any more. Linking happens on
    // the security page, which is FoF OAuth's own UI, so this section reports
    // status and points at it rather than collecting anything itself.
    const body = verified ? [m("p", {
      className: "jrh-discordLink-verified"
    }, m("i", {
      className: "icon fab fa-discord"
    }), " ", app.translator.trans('jrh-discord-link.forum.settings.verified'))] : [m("p", {
      className: "jrh-discordLink-value jrh-discordLink-value--unlinked"
    }, app.translator.trans('jrh-discord-link.forum.settings.not_linked')),
    // /u/<slug>/security, which is the route core's own "Security" link
    // uses and the one FoF OAuth hangs its linked-accounts section off
    // (its extender is addLinkedAccountsToUserSecurityPage). /settings/
    // security is also registered but does not resolve on its own here -
    // it renders Flarum's not-found page.
    m("a", {
      className: "Button Button--primary",
      href: app.route('user.security', {
        username: user.slug()
      })
    }, m("i", {
      className: "icon fab fa-discord"
    }), " ", app.translator.trans('jrh-discord-link.forum.settings.connect'))];
    items.add('jrh-discord-link', m(FieldSet, {
      className: "Settings-discordLink FieldSet--min",
      label: app.translator.trans('jrh-discord-link.forum.settings.heading')
    }, m("p", {
      className: "helpText"
    }, app.translator.trans('jrh-discord-link.forum.settings.help')), body),
    // Higher priority renders FIRST. Core numbers its own sections
    // `100 - index * 10` (account 100, notifications 90, privacy 80,
    // device 70, colour scheme 60), so 110 puts linking above Account -
    // it is the one setting we actively want members to complete.
    110);

    // RetroAchievements handle, display only. Members set it by running
    // /completed with `username:theirRAname`, which verifies it against
    // RetroAchievements before storing - so there is deliberately no input
    // here to overwrite a verified value with an unverified one.
    const raUsername = user.attribute('raUsername');
    items.add('jrh-ra-account', m(FieldSet, {
      className: "Settings-raAccount FieldSet--min",
      label: app.translator.trans('jrh-discord-link.forum.ra.heading')
    }, raUsername ? m("p", {
      className: "jrh-discordLink-value"
    }, m("i", {
      className: "icon fas fa-trophy"
    }), " ", raUsername) : m("p", {
      className: "helpText"
    }, app.translator.trans('jrh-discord-link.forum.ra.none')), m("p", {
      className: "helpText"
    }, app.translator.trans('jrh-discord-link.forum.ra.help'))),
    // Directly below the Discord section (110), above core's Account (100).
    109);

    // Club standing, mirrored from the bot. Read-only for the same reason
    // the two fields above are: every number here is derived from
    // completions, masteries and thread_activity in the bot's own
    // database, none of which the forum can see.
    //
    // Parsed defensively - it arrives as a JSON string written by another
    // system, and a settings page that throws is a far worse failure than
    // one section quietly not rendering.
    let activity = null;
    try {
      activity = JSON.parse(user.attribute('clubActivity') || 'null');
    } catch (e) {
      activity = null;
    }
    if (activity && Array.isArray(activity.events) && activity.events.length) {
      items.add('jrh-club-activity', m(FieldSet, {
        className: "Settings-clubActivity FieldSet--min",
        label: app.translator.trans('jrh-discord-link.forum.activity.heading')
      }, m("ul", {
        className: "jrh-activity-list"
      }, activity.events.map(entry => m("li", {
        className: "jrh-activity-row"
      }, m("span", {
        className: "jrh-activity-event"
      }, entry.label), m("span", {
        className: "jrh-activity-detail"
      }, entry.weight != null ? m("strong", null, app.translator.trans('jrh-discord-link.forum.activity.weight', {
        weight: entry.weight.toFixed(2)
      })) : null, m("span", {
        className: "jrh-activity-endscreen"
      }, entry.end_screen ? app.translator.trans('jrh-discord-link.forum.activity.end_screen_yes') : app.translator.trans('jrh-discord-link.forum.activity.end_screen_no')))))), m("p", {
        className: "helpText"
      }, app.translator.trans('jrh-discord-link.forum.activity.help'))), 108);
    }
  });

  // Shown after the session is known. Guarded so it fires once per page load
  // rather than on every route change, which would re-add the alert each time
  // the member clicked anything.
  let promptShown = false;
  extend('flarum/forum/components/IndexPage', 'oninit', () => {
    if (promptShown) return;
    promptShown = true;
    maybeShowConnectPrompt();
  });

  // The signup form used to carry a Discord username box. It is gone: a
  // typed handle cannot be verified, and FoF OAuth already puts "Sign up with
  // Discord" on this very modal, which links the account properly in one
  // step. Anyone who registers with an email instead links afterwards from
  // the security page - the settings section above points them there.

  // Admin view of a member's linked accounts, from their profile -> Edit.
  // Read-only now: an admin cannot type someone's Discord identity in on
  // their behalf, because only Discord can confirm it. Unlinking, if it is
  // ever needed, is FoF OAuth's "moderate user's linked accounts" permission.
  extend('flarum/common/components/EditUserModal', 'oninit', function () {
    this.jrhDiscordVerified = this.attrs.user.attribute('discordVerified');
    this.jrhRaUsername = this.attrs.user.attribute('raUsername') || '';
  });
  extend('flarum/common/components/EditUserModal', 'fields', function (items) {
    // Mods can reach this modal too, but the server only accepts these two
    // from an admin. Showing inputs that always fail would be worse than
    // showing none, so they are gated on the same condition the server uses.
    if (!app.session.user || !app.session.user.isAdmin()) return;
    items.add('jrh-discord-username', m("div", {
      className: "Form-group"
    }, m("label", null, app.translator.trans('jrh-discord-link.forum.admin.discord_label')), m("div", {
      className: "jrh-adminReadonly"
    }, this.jrhDiscordVerified ? m("span", null, m("i", {
      className: "icon fab fa-discord"
    }), " ", app.translator.trans('jrh-discord-link.forum.settings.verified')) : m("span", {
      className: "jrh-adminReadonly--empty"
    }, app.translator.trans('jrh-discord-link.forum.settings.not_linked'))), m("p", {
      className: "helpText"
    }, app.translator.trans('jrh-discord-link.forum.admin.discord_help'))), -10);

    // Shown, not editable. The bot's ra_usernames table is the source of
    // truth and the identity sweep pushes it here, so anything typed into a
    // box would be silently reverted within minutes. Members change it by
    // logging a completion with a different username, which is checked
    // against RetroAchievements before it is accepted - a plain input here
    // would bypass that verification even if it did persist.
    items.add('jrh-ra-username', m("div", {
      className: "Form-group"
    }, m("label", null, app.translator.trans('jrh-discord-link.forum.admin.ra_label')), m("div", {
      className: "jrh-adminReadonly"
    }, this.jrhRaUsername ? m("span", null, m("i", {
      className: "icon fas fa-trophy"
    }), " ", this.jrhRaUsername) : m("span", {
      className: "jrh-adminReadonly--empty"
    }, app.translator.trans('jrh-discord-link.forum.ra.none'))), m("p", {
      className: "helpText"
    }, app.translator.trans('jrh-discord-link.forum.admin.ra_help'))), -11);
  });

  // Nothing to submit: both fields in this modal are displayed only. The
  // Discord link belongs to Discord, and raUsername is verified by the bot
  // against RetroAchievements before it is stored.
});

// 2.0 registers an extension's frontend from its entry-point exports.
export default {};